import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react'
import {
  LoadManager, PageHeader, Styles, Form,
} from '@/components'
import usePageTitle from '@/hooks/usePageTitle'
import { useHistory } from 'react-router-dom'
import passwordReset from '@/services/auth/passwordReset'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import partnerLicensings from '@/services/api/settings/partnerLicensings'
import yup from '@/lib/yupPt'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import Email from './components/Email'
import NewPassword from './components/NewPassword'

const schemas = {
  1: yup.object().shape({
    token: yup
      .string()
      .length(6, 'O código deve ter 6 dígitos')
      .required('Código é obrigatório'),
  }),
  2: yup.object().shape({
    password: yup
      .string()
      .min(6, 'A senha deve ter no mínimo 6 caracteres')
      .required('Senha é obrigatória'),
    password_confirmation: yup
      .string()
      .oneOf([yup.ref('password')], 'As senhas devem ser iguais')
      .required('Confirmação de senha é obrigatória'),
  }),
}

function ResetPassword({ title }) {
  usePageTitle({ title })

  const history = useHistory()
  const {
    data, loading, error,
  } = useAxiosRequest(partnerLicensings.current)
  const email = data?.licensed?.email

  const [step, setStep] = useState(1)
  const [emailSent, setEmailSent] = useState(false)
  const [resettingPassword, setResettingPassword] = useState(false)
  const formRef = useRef(null)

  const schema = useMemo(() => schemas[step], [step])

  useEffect(() => {
    formRef.current?.validateForm()
  }, [schema])

  const renderStep = useCallback(
    ({
      values, errors, touched, setFieldValue,
    }) => {
      if (step === 1) {
        return (
          <Email
            email={email}
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            onEmailSentChange={setEmailSent}
          />
        )
      }

      if (step === 2) {
        return (
          <NewPassword
            values={values}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
          />
        )
      }

      return null
    },
    [step, email],
  )

  async function onSubmit(values) {
    if (!email) return

    try {
      setResettingPassword(true)
      await passwordReset.reset({
        login: email,
        password: values.password,
        token: values.token,
      })
      toast.success('Senha alterada com sucesso')
      history.goBack()
    } catch (e) {
      toast.error(e.message)
      if (e === 'wrongToken' || e === 'tokenExpirated') {
        setStep(1)
      }
    } finally {
      setResettingPassword(false)
    }
  }

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Redefinir senha"
        icon="fas fa-key"
        subtitle="Redefina sua senha de acesso ao sistema"
      />
      <LoadManager loading={loading} error={error}>
        <fieldset disabled={resettingPassword}>
          <Form.Formik
            innerRef={formRef}
            initialValues={{
              token: '',
              password: '',
              password_confirmation: '',
            }}
            validationSchema={schema}
            onSubmit={onSubmit}
            validateOnMount
          >
            {({
              values, errors, touched, isValid, setFieldValue,
            }) => (
              <Form.ValidationForm>
                <div className="panel">
                  <div className="panel-header" />
                  <div className="panel-nav">
                    <ul className="step">
                      <li className={`step-item ${step === 1 ? 'active' : ''}`}>
                        <a className="tooltip" data-tooltip="Código">
                          Código
                        </a>
                      </li>
                      <li className={`step-item ${step === 2 ? 'active' : ''}`}>
                        <a className="tooltip" data-tooltip="Nova senha">
                          Nova senha
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="panel-body mt-2">
                    <div className="divider" />
                    <div>
                      {renderStep({
                        values,
                        errors,
                        touched,
                        isValid,
                        setFieldValue,
                      })}
                    </div>
                  </div>
                  <div className="panel-footer text-right">
                    <div className="divider" />
                    <>
                      {step > 1 && (
                        <button
                          type="button"
                          onClick={() => setStep(step - 1)}
                          className="btn btn-primary mr-2"
                        >
                          <i className="fas fa-chevron-left" />
                          &nbsp;Anterior
                        </button>
                      )}
                      {step < 2 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            if (!isValid || !emailSent) {
                              e.preventDefault()
                            } else {
                              setStep(step + 1)
                            }
                          }}
                          className={`btn btn-primary ${
                            !isValid || !emailSent ? 'disabled' : ''
                          }`}
                        >
                          Avançar&nbsp;
                          <i className="fas fa-chevron-right" />
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className={`btn btn-success ${resettingPassword ? 'loading' : ''}`}
                        >
                          <i className="fas fa-check" />
                          &nbsp;Alterar senha
                        </button>
                      )}
                    </>
                  </div>
                </div>
              </Form.ValidationForm>
            )}
          </Form.Formik>
        </fieldset>
      </LoadManager>
    </Styles.Container>
  )
}

ResetPassword.propTypes = {
  title: PropTypes.string.isRequired,
}

export default ResetPassword
